import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { pmGet, pmPost } from '../../api';
import './AgileWorkspace.css';
import { SelectField, PROMPT } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../../../portfolio_definition/HelpPanel';
import { withFinalizedRule } from '../../finalizedRuleHelp';

/**
 * Agile Delivery Workspace — one opportunity's MVP waves and its Epic → Feature → Story →
 * Task backlog.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/roadmap-execution/agile-workspace.ejs`.
 *
 * A RAIL AND A BOARD. The left rail is one tile per wave plus an ALL tile; the right is that
 * wave's backlog on a single surface with dividers, not nested cards. The wave's colour is
 * carried as the CSS custom property `--mc` so its rail tile, band, tabs and rows all share
 * one identity.
 *
 * THE ALL VIEW IS NOT A SUM — it is every wave's epics listed together, each tagged with the
 * wave it belongs to. That is why `+ Epic` disappears in ALL: a new epic has to belong to a
 * specific wave, and there is no way to know which one is meant.
 *
 * SEARCH KEEPS ANCESTORS. A story that matches drags its feature and epic into the result
 * even when neither matched, because a bare story with no parent is not locatable. Typing in
 * the box also force-opens every epic and feature so matches are visible without clicking.
 *
 * EDITING IS A RIGHT-HAND DRAWER shared by all four entity kinds. It closes on Escape, and
 * its Save controls are explicitly re-enabled on open — the source's comment records why: a
 * successful save closes the drawer rather than restoring it, so without the reset the NEXT
 * open would have a dead Save button.
 */

const MVP_COLORS = [
  '#00838f',
  '#1565c0',
  '#6a1b9a',
  '#ef6c00',
  '#2e7d32',
  '#5e35b1',
  '#c62828',
  '#00897b',
  '#7cb342',
  '#3949ab',
];
const STREAM_C: Record<string, { bg: string; fg: string }> = {
  Finance: { bg: '#e3f2fd', fg: '#1565c0' },
  Data: { bg: '#e0f2f1', fg: '#00838f' },
  Program: { bg: '#ede7f6', fg: '#5e35b1' },
};
const STATUS_OPTS = ['To Do', 'In Progress', 'Done', 'Blocked', 'Not Started'];
const KIND_LABEL: Record<string, string> = {
  epic: 'Epic',
  feature: 'Feature',
  story: 'User Story',
  task: 'Task',
};

/** `st-InProgress` etc. — the class is the status with non-letters stripped. */
const stCls = (s?: string | null) => `st-${String(s || 'To Do').replace(/[^A-Za-z]/g, '')}`;
const taskDot = (s?: string | null) =>
  s === 'Done' ? '#43a047' : s === 'In Progress' ? '#fb8c00' : '#cfd8dc';

interface Task {
  id: number;
  description: string;
  owner: string | null;
  status: string;
}
interface Story {
  id: number;
  epic_id: number | null;
  feature_id: number | null;
  title: string | null;
  as_a: string | null;
  i_want: string | null;
  so_that: string | null;
  acceptance_criteria: string | null;
  story_points: number | null;
  priority: string | null;
  status: string;
  tasks?: Task[];
}
interface Feature {
  id: number;
  epic_id: number;
  name: string;
  description: string | null;
  status: string;
  stories?: Story[];
}
interface Epic {
  id: number;
  mvp_seq: number | null;
  name: string;
  description: string | null;
  status: string;
  features?: Feature[];
  stories_direct?: Story[];
}
interface Deliverable {
  id: number;
  name: string;
  stream: string | null;
  status: string;
}
interface Wave {
  seq: number;
  name: string;
  start: string | null;
  end: string | null;
  sprintCount: number;
  epics: Epic[];
  deliverables: Deliverable[];
  stats: {
    epics: number;
    features: number;
    stories: number;
    points: number;
    pointsDone: number;
    deliverables: number;
    deliverablesInScope: number;
    progress: number;
  };
}
interface WorkspaceData {
  success: boolean;
  error?: string;
  opportunity: { delivery_project_id: string; opportunity_id: string; name: string };
  cadence: { sprintsPerMvp: number; sprintWeeks: number };
  mvps: Wave[];
  totals: { stories: number; points: number; pointsDone: number };
}
interface ProjectOption {
  delivery_project_id: string;
  description: string | null;
  opportunities: { opportunity_id: string; name: string }[];
}

type Kind = 'epic' | 'feature' | 'story' | 'task';
interface EditState {
  kind: Kind;
  mode: 'new' | 'edit';
  ctx: Record<string, number | undefined>;
  ent: Epic | Feature | Story | Task | null;
}

/**
 * "How this works" — cloned from `ai_readiness_template/views/roadmap-execution/agile-workspace.ejs`,
 * its `agileWorkspaceHelp` object. Copy, colours and section order are the source's;
 * only the link hrefs are rewritten to this platform's routes, and the entities in
 * fields HelpPanel renders as TEXT (title, section label, term, link label) are
 * decoded, because only the HTML fields go through `dangerouslySetInnerHTML`.
 */
const AGILE_HELP: HelpContent = {
    title: 'Agile Delivery Workspace',
    icon: '🏃',
    purpose: "The delivery cockpit for an <strong>Agile opportunity</strong> — and where you <strong>build and manage its backlog</strong>. The screen is split in two: a <strong>rail of MVP waves</strong> on the left, and the selected wave's <strong>backlog</strong> on the right. Waves come straight from the cadence you set on <strong>Definition</strong> (weeks per sprint × sprints per MVP) and the names you gave them on <strong>Definition / Finalization</strong>. Each wave keeps its own colour, so a wave looks the same here as everywhere else.",
    doHere: [
      "Pick a <strong>project</strong>, then one of its <strong>Agile opportunities</strong> — both selectors sit at the top of the left rail.",
      "Click a <strong>wave tile</strong> in the rail to load it on the right; <strong>ALL MVP waves</strong> shows the whole backlog at once, with a wave badge on every epic.",
      "The header carries the wave's dates, sprints and its counts — epics, features, stories, story points and work items.",
      "Switch between <strong>Backlog</strong> and <strong>Work items</strong> with the tabs. Use the filter to narrow by any epic, feature or story name; matches expand automatically.",
      "Expand an <strong>Epic</strong> for its features and stories, and a <strong>Story</strong> for its narrative, acceptance criteria and tasks.",
      "<strong>Build the backlog here</strong> — hover any row for its actions: <strong>+ Epic</strong> on the toolbar, <strong>+ Feature</strong> / <strong>+ Story</strong> on an epic, <strong>+ Story</strong> on a feature, <strong>+ Task</strong> on a story, plus <strong>✎ edit</strong> and <strong>🗑 delete</strong> on every item. Editing opens a panel on the right, so the backlog stays visible. A story always lives in its epic's MVP wave; deleting a parent removes everything under it."
    ],
    fits: 'Definition (methodology + cadence) &rarr; Finalization (MVP schedule &amp; names) &rarr; <strong>Agile Delivery Workspace</strong>',
    firstTime: "Pick project 3664 and opportunity 9714 to see a fully worked example: 3 MVP waves in the rail, an epic/feature/story backlog on the right, and work items per wave.",
    sections: [
      {
        label: 'The Agile hierarchy',
        type: 'defs',
        items: [
          { term: 'MVP wave', color: '#00838f', def: "A shippable increment (MVP 0 Foundation → 1 First value → 2 Enhancements …). Generated from the cadence; named on Definition/Finalization." },
          { term: 'Epic', color: '#6a1b9a', def: "A large body of work inside an MVP, broken into Features." },
          { term: 'Feature', color: '#1565c0', def: "A shippable capability inside an Epic, delivered by User Stories." },
          { term: 'User Story', color: '#2e7d32', def: "A unit of value — <em>As a … I want … so that …</em> — with story points, priority and status." }
        ]
      }
    ],
    links: [ { label: 'Set methodology & cadence on Definition', href: '/project_management/portfolio/project-setup/project-creation' } ]
  };

export function AgileWorkspace() {
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [projId, setProjId] = useState('');
  const [oppId, setOppId] = useState('');
  const [data, setData] = useState<WorkspaceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activeMvp, setActiveMvp] = useState<number | 'all'>('all');
  const [activeTab, setActiveTab] = useState<'backlog' | 'items'>('backlog');
  const [q, setQ] = useState('');
  const [openSet, setOpenSet] = useState<Record<string, boolean>>({});

  const [edit, setEdit] = useState<EditState | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  /** id → entity, for edit prefill and delete labels. The source's `IX`. */
  const index = useMemo(() => {
    const ix = {
      epic: {} as Record<number, Epic>,
      feature: {} as Record<number, Feature>,
      story: {} as Record<number, Story>,
      task: {} as Record<number, Task>,
    };
    (data?.mvps ?? []).forEach((m) =>
      (m.epics ?? []).forEach((e) => {
        ix.epic[e.id] = e;
        (e.features ?? []).forEach((f) => {
          ix.feature[f.id] = f;
          (f.stories ?? []).forEach((s) => {
            ix.story[s.id] = s;
            (s.tasks ?? []).forEach((t) => (ix.task[t.id] = t));
          });
        });
        (e.stories_direct ?? []).forEach((s) => {
          ix.story[s.id] = s;
          (s.tasks ?? []).forEach((t) => (ix.task[t.id] = t));
        });
      }),
    );
    return ix;
  }, [data]);

  const loadOpp = useCallback(async (dp: string, opp: string) => {
    if (!dp || !opp) return;
    setLoading(true);
    setError(null);
    try {
      const d = await pmGet<WorkspaceData>(
        `/agile-opp/data?delivery_project_id=${encodeURIComponent(dp)}&opportunity_id=${encodeURIComponent(opp)}`,
      );
      if (!d.success) {
        setError(d.error ?? 'Could not load.');
        setData(null);
        return;
      }
      setData(d);
      setActiveMvp('all');
      setOpenSet({});
      setQ('');
      setActiveTab('backlog');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    pmGet<{ projects: ProjectOption[] }>('/agile-opp/projects')
      .then((d) => {
        const list = d.projects ?? [];
        setProjects(list);
        // The ECC's Backlog tab links here with the programme (and sometimes the opportunity).
        const pid = searchParams.get('delivery_project_id');
        if (pid && list.some((p) => p.delivery_project_id === pid)) {
          setProjId(pid);
          const p = list.find((x) => x.delivery_project_id === pid)!;
          const oid = searchParams.get('opportunity_id');
          const chosen =
            oid && p.opportunities.some((o) => o.opportunity_id === oid)
              ? oid
              : (p.opportunities[0]?.opportunity_id ?? '');
          if (chosen) {
            setOppId(chosen);
            void loadOpp(pid, chosen);
          }
        }
      })
      .catch((e) => setError((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onProj = (pid: string) => {
    setProjId(pid);
    setData(null);
    const p = projects.find((x) => x.delivery_project_id === pid);
    if (!p || !p.opportunities.length) {
      setOppId('');
      return;
    }
    const first = p.opportunities[0]!.opportunity_id;
    setOppId(first);
    void loadOpp(pid, first);
  };

  const waves = data?.mvps ?? [];
  const isAll = activeMvp === 'all';
  const waveColor = (seq: number | 'all') =>
    seq === 'all' ? '#607d8b' : MVP_COLORS[seq % MVP_COLORS.length]!;
  const selWaves = isAll ? waves : waves.filter((m) => String(m.seq) === String(activeMvp));
  const hit = (t?: string | null) => !q || String(t || '').toLowerCase().indexOf(q) >= 0;

  const toggleRow = (kind: string, id: number) =>
    setOpenSet((prev) => ({ ...prev, [kind + id]: !prev[kind + id] }));

  const onFilter = (v: string) => {
    const next = String(v || '').trim().toLowerCase();
    setQ(next);
    if (next) {
      // Force every epic and feature open so matches are visible without clicking.
      setOpenSet((prev) => {
        const o = { ...prev };
        (isAll ? waves : waves.filter((m) => String(m.seq) === String(activeMvp))).forEach((m) =>
          (m.epics ?? []).forEach((e) => {
            o[`e${e.id}`] = true;
            (e.features ?? []).forEach((f) => (o[`f${f.id}`] = true));
          }),
        );
        return o;
      });
    }
  };

  /** Collapse everything if anything is open, else open everything. The source's `expandAll`. */
  const expandAll = () => {
    let any = false;
    selWaves.forEach((m) => (m.epics ?? []).forEach((e) => {
      if (openSet[`e${e.id}`]) any = true;
    }));
    setOpenSet(() => {
      const o: Record<string, boolean> = {};
      selWaves.forEach((m) =>
        (m.epics ?? []).forEach((e) => {
          o[`e${e.id}`] = !any;
          (e.features ?? []).forEach((f) => (o[`f${f.id}`] = !any));
        }),
      );
      return o;
    });
  };

  /* ---------- editor ---------- */

  const openEd = (kind: Kind, mode: 'new' | 'edit', ctx: Record<string, number | undefined>) => {
    if (!projId || !oppId) {
      setError('Select a project and opportunity first.');
      return;
    }
    let ent: Epic | Feature | Story | Task | null = null;
    if (mode === 'edit') {
      ent = (index[kind] as Record<number, never>)[ctx.id!] ?? null;
      if (!ent) {
        setError('Item not found — reload and try again.');
        return;
      }
    }
    const e = ent as Record<string, unknown> | null;
    setForm({
      wave: String(
        (e?.mvp_seq as number | undefined) ?? ctx.mvp_seq ?? 0,
      ),
      name: String(e?.name ?? ''),
      desc: String(e?.description ?? ''),
      title: String(e?.title ?? ''),
      asa: String(e?.as_a ?? ''),
      iwant: String(e?.i_want ?? ''),
      sothat: String(e?.so_that ?? ''),
      ac: String(e?.acceptance_criteria ?? ''),
      pts: String(Number(e?.story_points) || 0),
      prio: String(e?.priority ?? 'Medium'),
      owner: String(e?.owner ?? ''),
      taskDesc: String(e?.description ?? ''),
      status: String(e?.status ?? 'To Do'),
    });
    setBusy(false);
    setEdit({ kind, mode, ctx, ent });
  };

  const closeEd = () => {
    setEdit(null);
    setBusy(false);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && edit) closeEd();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [edit]);

  const saveEd = async () => {
    if (!edit) return;
    const { kind, mode, ctx, ent } = edit;
    const e = ent as Record<string, unknown> | null;
    const base: Record<string, unknown> = {
      delivery_project_id: projId,
      opportunity_id: oppId,
    };
    if (mode === 'edit') base.id = ctx.id;
    let payload: Record<string, unknown>;
    if (kind === 'epic') {
      if (!form.name!.trim()) {
        setError('Epic name is required.');
        return;
      }
      payload = {
        ...base,
        mvp_seq: +form.wave!,
        name: form.name,
        description: form.desc,
        status: form.status,
      };
    } else if (kind === 'feature') {
      if (!form.name!.trim()) {
        setError('Feature name is required.');
        return;
      }
      payload = {
        ...base,
        epic_id: ctx.epic_id ?? (e?.epic_id as number | undefined),
        name: form.name,
        description: form.desc,
        status: form.status,
      };
    } else if (kind === 'story') {
      if (!form.title!.trim()) {
        setError('Story title is required.');
        return;
      }
      payload = {
        ...base,
        epic_id: ctx.epic_id ?? (e?.epic_id as number | undefined),
        feature_id: ctx.feature_id ?? (e?.feature_id as number | undefined) ?? null,
        title: form.title,
        as_a: form.asa,
        i_want: form.iwant,
        so_that: form.sothat,
        acceptance_criteria: form.ac,
        story_points: +form.pts! || 0,
        priority: form.prio,
        status: form.status,
      };
    } else {
      if (!form.taskDesc!.trim()) {
        setError('Task description is required.');
        return;
      }
      payload = {
        ...base,
        story_id: ctx.story_id ?? (e?.story_id as number | undefined),
        description: form.taskDesc,
        owner: form.owner,
        status: form.status,
      };
    }
    setBusy(true);
    try {
      const r = await pmPost<{ success: boolean; error?: string }>(
        `/agile-opp/${kind}/save`,
        payload,
      );
      if (!r.success) {
        setError(r.error ?? 'request failed');
        setBusy(false);
        return;
      }
      closeEd();
      await loadOpp(projId, oppId);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  const delEnt = async (kind: Kind, id: number) => {
    if (!projId) return;
    const cascade: Record<Kind, string> = {
      epic: ' and all its features, stories and tasks',
      feature: ' and all its stories and tasks',
      story: ' and all its tasks',
      task: '',
    };
    const ent = ((index[kind] as Record<number, never>)[id] ?? {}) as Record<string, unknown>;
    const label = (ent.name ?? ent.title ?? ent.description ?? '') as string;
    if (
      !window.confirm(
        `Delete this ${KIND_LABEL[kind]!.toLowerCase()}${label ? ` “${label}”` : ''}${cascade[kind]}?\n\nThis cannot be undone.`,
      )
    )
      return;
    try {
      const r = await pmPost<{ success: boolean; error?: string }>(`/agile-opp/${kind}/delete`, {
        delivery_project_id: projId,
        id,
      });
      if (!r.success) {
        setError(r.error ?? 'request failed');
        return;
      }
      await loadOpp(projId, oppId);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  /* ---------- rows ---------- */

  const StatusChip = ({ s }: { s?: string | null }) => (
    <span className={`pm-aw-chip ${stCls(s)}`}>{s || 'To Do'}</span>
  );

  const storyRow = (m: Wave, e: Epic, f: Feature | null, s: Story) => {
    const tasks = (s.tasks ?? []).map((t) => (
      <div className="task" key={t.id}>
        <span className="tk" style={{ background: taskDot(t.status) }} />
        <span style={{ flex: 1, minWidth: 0 }}>
          {t.description}
          {t.owner ? <span style={{ color: '#90a4ae' }}> · {t.owner}</span> : null}
        </span>
        <span className="acts" style={{ opacity: 1 }}>
          <button className="ba" title="Edit task" onClick={() => openEd('task', 'edit', { id: t.id })}>
            ✎
          </button>
          <button className="ba del" title="Delete task" onClick={() => void delEnt('task', t.id)}>
            🗑
          </button>
        </span>
      </div>
    ));
    const narr =
      s.as_a || s.i_want || s.so_that ? (
        <div className="narr">
          <b>As a</b> {s.as_a || '…'} <b>I want</b> {s.i_want || '…'} <b>so that</b>{' '}
          {s.so_that || '…'}
        </div>
      ) : null;
    const ac = s.acceptance_criteria ? <div className="ac">✓ {s.acceptance_criteria}</div> : null;
    const hasDetail = !!(narr || ac || tasks.length);
    return (
      <div className={`row-story${openSet[`s${s.id}`] ? ' open' : ''}`} key={s.id}>
        <div
          className="grid"
          role="button"
          tabIndex={0}
          onClick={() => toggleRow('s', s.id)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              ev.preventDefault();
              toggleRow('s', s.id);
            }
          }}
        >
          <span className="lead">
            <span className="caret">▶</span>
            <span className="tag s">S</span>
            <span className="nm">{s.title}</span>
          </span>
          <span className="c-pri">
            {s.priority ? <span className={`pri-${s.priority}`}>{s.priority}</span> : null}
          </span>
          <span className="pts">{Number(s.story_points) || 0}</span>
          <span>
            <StatusChip s={s.status} />
          </span>
          <span className="acts">
            <button
              className="ba"
              title="Add task"
              onClick={(ev) => {
                ev.stopPropagation();
                openEd('task', 'new', { story_id: s.id });
              }}
            >
              + Task
            </button>
            <button
              className="ba"
              title="Edit story"
              onClick={(ev) => {
                ev.stopPropagation();
                openEd('story', 'edit', { id: s.id });
              }}
            >
              ✎
            </button>
            <button
              className="ba del"
              title="Delete story"
              onClick={(ev) => {
                ev.stopPropagation();
                void delEnt('story', s.id);
              }}
            >
              🗑
            </button>
          </span>
        </div>
        <div className="kids">
          {hasDetail ? (
            <>
              {narr}
              {ac}
              {tasks}
            </>
          ) : (
            <div className="empty-mini">No narrative or tasks yet.</div>
          )}
        </div>
      </div>
    );
  };

  const featRow = (m: Wave, e: Epic, f: Feature) => {
    const fHit = hit(e.name) || hit(f.name);
    const kids = (f.stories ?? []).filter((s) => fHit || hit(s.title));
    if (!fHit && !kids.length) return null;
    const pts = (f.stories ?? []).reduce((n, s) => n + (Number(s.story_points) || 0), 0);
    return (
      <div className={`row-feat${openSet[`f${f.id}`] ? ' open' : ''}`} key={f.id}>
        <div
          className="grid"
          role="button"
          tabIndex={0}
          onClick={() => toggleRow('f', f.id)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              ev.preventDefault();
              toggleRow('f', f.id);
            }
          }}
        >
          <span className="lead">
            <span className="caret">▶</span>
            <span className="tag f">F</span>
            <span className="nm">{f.name}</span>
          </span>
          <span className="sub c-pri">{(f.stories ?? []).length} stories</span>
          <span className="pts">{pts}</span>
          <span>
            <StatusChip s={f.status} />
          </span>
          <span className="acts">
            <button
              className="ba"
              title="Add user story"
              onClick={(ev) => {
                ev.stopPropagation();
                openEd('story', 'new', { epic_id: e.id, feature_id: f.id });
              }}
            >
              + Story
            </button>
            <button
              className="ba"
              title="Edit feature"
              onClick={(ev) => {
                ev.stopPropagation();
                openEd('feature', 'edit', { id: f.id });
              }}
            >
              ✎
            </button>
            <button
              className="ba del"
              title="Delete feature"
              onClick={(ev) => {
                ev.stopPropagation();
                void delEnt('feature', f.id);
              }}
            >
              🗑
            </button>
          </span>
        </div>
        <div className="kids">
          {kids.length ? (
            kids.map((s) => storyRow(m, e, f, s))
          ) : (
            <div className="empty-mini">No stories yet.</div>
          )}
        </div>
      </div>
    );
  };

  const epicRow = (m: Wave, e: Epic) => {
    const eHit = hit(e.name);
    const feats = (e.features ?? []).map((f) => featRow(m, e, f)).filter(Boolean);
    const direct = (e.stories_direct ?? [])
      .filter((s) => eHit || hit(s.title))
      .map((s) => storyRow(m, e, null, s));
    if (!eHit && !feats.length && !direct.length) return null;
    const hasBody = feats.length > 0 || direct.length > 0;
    return (
      <div
        className={`row-epic${openSet[`e${e.id}`] ? ' open' : ''}`}
        style={{ ['--mc' as string]: waveColor(m.seq) }}
        key={e.id}
      >
        <div
          className="grid"
          role="button"
          tabIndex={0}
          onClick={() => toggleRow('e', e.id)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              ev.preventDefault();
              toggleRow('e', e.id);
            }
          }}
        >
          <span className="lead">
            <span className="caret">▶</span>
            <span className="tag">E</span>
            {/* In the ALL view every epic carries the wave it belongs to. */}
            {isAll ? (
              <span
                className="pm-aw-wave-chip"
                style={{ color: waveColor(m.seq), borderColor: waveColor(m.seq) }}
              >
                MVP {m.seq}
              </span>
            ) : null}
            <span className="nm">{e.name}</span>
          </span>
          <span className="sub c-pri">{(e.features ?? []).length} features</span>
          <span className="pts" />
          <span>
            <StatusChip s={e.status} />
          </span>
          <span className="acts">
            <button
              className="ba"
              title="Add feature"
              onClick={(ev) => {
                ev.stopPropagation();
                openEd('feature', 'new', { epic_id: e.id });
              }}
            >
              + Feature
            </button>
            <button
              className="ba"
              title="Add user story"
              onClick={(ev) => {
                ev.stopPropagation();
                openEd('story', 'new', { epic_id: e.id });
              }}
            >
              + Story
            </button>
            <button
              className="ba"
              title="Edit epic"
              onClick={(ev) => {
                ev.stopPropagation();
                openEd('epic', 'edit', { id: e.id });
              }}
            >
              ✎
            </button>
            <button
              className="ba del"
              title="Delete epic"
              onClick={(ev) => {
                ev.stopPropagation();
                void delEnt('epic', e.id);
              }}
            >
              🗑
            </button>
          </span>
        </div>
        <div className="kids">
          {hasBody ? (
            <>
              {feats}
              {direct}
            </>
          ) : (
            <div className="empty-mini">No features or stories yet — add one.</div>
          )}
        </div>
      </div>
    );
  };

  /* ---------- panel ---------- */

  const one = isAll ? null : selWaves[0];
  const c = waveColor(isAll ? 'all' : (one?.seq ?? 0));
  const agg = selWaves.reduce(
    (a, m) => ({
      epics: a.epics + m.stats.epics,
      features: a.features + m.stats.features,
      stories: a.stories + m.stats.stories,
      points: a.points + m.stats.points,
      pointsDone: a.pointsDone + m.stats.pointsDone,
      deliverables: a.deliverables + m.stats.deliverables,
    }),
    { epics: 0, features: 0, stories: 0, points: 0, pointsDone: 0, deliverables: 0 },
  );
  const pct = agg.points > 0 ? Math.round((agg.pointsDone / agg.points) * 100) : 0;
  const sprints = selWaves.reduce((n, m) => n + (m.sprintCount || 0), 0);
  const totals = data?.totals ?? { stories: 0, points: 0, pointsDone: 0 };
  const allPct = totals.points > 0 ? Math.round((totals.pointsDone / totals.points) * 100) : 0;

  const backlogRows = selWaves.flatMap((m) => (m.epics ?? []).map((e) => epicRow(m, e))).filter(Boolean);

  const itemRows = selWaves.flatMap((m) =>
    (m.deliverables ?? [])
      .filter((dv) => hit(dv.name))
      .map((dv) => {
        const sc = STREAM_C[dv.stream ?? ''] ?? { bg: '#eceff1', fg: '#607d8b' };
        return (
          <div className="wi" key={dv.id}>
            <span className="nm">{dv.name}</span>
            {dv.stream ? (
              <span className="wi-stream" style={{ background: sc.bg, color: sc.fg }}>
                {dv.stream}
              </span>
            ) : (
              <span />
            )}
            <span>
              <StatusChip s={dv.status} />
            </span>
          </div>
        );
      }),
  );

  const field = (k: string) => form[k] ?? '';
  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <>
      <HelpPanel help={withFinalizedRule(AGILE_HELP)} />
      <div className="pm-aw">
        <div className="pm-aw-rail" role="navigation" aria-label="MVP wave selector">
          <div className="pm-aw-rail-hd">
            <SelectField
              label="Project"
              orientation="stacked"
              width="lg"
              placeholder={PROMPT.project}
              value={projId}
              onChange={onProj}
              options={projects.map((p) => ({
                value: p.delivery_project_id,
                id: p.delivery_project_id,
                description: p.description,
              }))}
            />
            {projId ? (
              <div>
                <div className="eyebrow">Agile opportunity</div>
                <select
                  className="pm-aw-sel"
                  value={oppId}
                  onChange={(e) => {
                    setOppId(e.target.value);
                    void loadOpp(projId, e.target.value);
                  }}
                >
                  {(projects.find((p) => p.delivery_project_id === projId)?.opportunities ?? []).map(
                    (o) => (
                      <option key={o.opportunity_id} value={o.opportunity_id}>
                        {o.opportunity_id}
                        {o.name && o.name !== o.opportunity_id
                          ? ` — ${o.name.length > 44 ? `${o.name.slice(0, 44)}…` : o.name}`
                          : ''}
                      </option>
                    ),
                  )}
                </select>
              </div>
            ) : null}
          </div>
          {waves.length ? (
            <>
              <div className="rail-list">
                <button
                  className={`mvp-tile all-tile${isAll ? ' active' : ''}`}
                  onClick={() => {
                    setActiveMvp('all');
                    setOpenSet({});
                  }}
                >
                  <span className="t-seq">ALL</span>
                  <div className="t-name">All MVP waves</div>
                  <div className="t-dates">
                    {waves.length} wave{waves.length === 1 ? '' : 's'} · full backlog
                  </div>
                  <div className="t-bar">
                    <i style={{ width: `${allPct}%` }} />
                  </div>
                  <div className="t-foot">
                    <span>{totals.stories} stories</span>
                    <span className="t-pct">{allPct}%</span>
                  </div>
                </button>
              </div>
              <div className="rail-div">MVP waves</div>
              <div className="rail-list">
                {waves.map((m) => (
                  <button
                    key={m.seq}
                    className={`mvp-tile${String(activeMvp) === String(m.seq) ? ' active' : ''}`}
                    style={{ ['--mc' as string]: waveColor(m.seq) }}
                    onClick={() => {
                      setActiveMvp(m.seq);
                      setOpenSet({});
                    }}
                  >
                    <span className="t-seq">MVP {m.seq}</span>
                    <div className="t-name">{m.name}</div>
                    <div className="t-dates">
                      {m.start || '?'} → {m.end || '?'}
                    </div>
                    <div className="t-bar">
                      <i style={{ width: `${m.stats.progress}%` }} />
                    </div>
                    <div className="t-foot">
                      <span>
                        {m.stats.stories} stories · {m.stats.pointsDone}/{m.stats.points} pts
                      </span>
                      <span className="t-pct">{m.stats.progress}%</span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : null}
        </div>

        <div className="pm-aw-panel">
          <div className="pm-aw-panelbox">
            {error ? <div className="pm-aw-empty" style={{ color: '#c62828' }}>{error}</div> : null}
            {loading ? (
              <div className="pm-aw-empty">Loading…</div>
            ) : !data ? (
              <div className="pm-aw-empty">
                Select a project with an Agile opportunity to open its delivery workspace.
              </div>
            ) : !waves.length ? (
              <div className="pm-aw-empty">No MVP waves on this opportunity yet.</div>
            ) : (
              <>
                <div className="pm-aw-band" style={{ ['--mc' as string]: c }}>
                  <div className="pm-aw-crumb">
                    {projId} · {oppId} ·{' '}
                    {isAll ? `all ${waves.length} waves` : `wave ${one!.seq + 1} of ${waves.length}`}
                  </div>
                  <div className="pm-aw-bandmain">
                    <div className="pm-aw-badge">{isAll ? 'ALL' : `MVP ${one!.seq}`}</div>
                    <div style={{ minWidth: 0 }}>
                      <h2>{isAll ? 'All MVP waves' : one!.name}</h2>
                      <div className="pm-aw-when">
                        {isAll
                          ? `${waves[0]!.start || '?'} → ${waves[waves.length - 1]!.end || '?'}`
                          : `${one!.start || '?'} → ${one!.end || '?'}`}{' '}
                        · {sprints} sprint{sprints === 1 ? '' : 's'} · {pct}% of points done
                      </div>
                    </div>
                  </div>
                  <ul className="pm-aw-kpis">
                    {(
                      [
                        ['Epics', agg.epics],
                        ['Features', agg.features],
                        ['Stories', agg.stories],
                        ['Story points', `${agg.pointsDone} / ${agg.points}`],
                        ['Work items', agg.deliverables],
                      ] as [string, string | number][]
                    ).map(([lbl, v]) => (
                      <li key={lbl}>
                        <b>{v}</b>
                        <span>{lbl}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pm-aw-tabs">
                  <button
                    className={`pm-aw-tab${activeTab === 'backlog' ? ' on' : ''}`}
                    style={{ ['--mc' as string]: c }}
                    onClick={() => setActiveTab('backlog')}
                  >
                    Backlog
                  </button>
                  <button
                    className={`pm-aw-tab${activeTab === 'items' ? ' on' : ''}`}
                    style={{ ['--mc' as string]: c }}
                    onClick={() => setActiveTab('items')}
                  >
                    Work items
                  </button>
                </div>

                <div className="pm-aw-tools" style={{ ['--mc' as string]: c }}>
                  <input
                    className="pm-aw-search"
                    type="search"
                    placeholder="Filter epics, features, stories…"
                    value={q}
                    onChange={(e) => onFilter(e.target.value)}
                    aria-label="Filter backlog"
                  />
                  <span className="pm-aw-spacer" />
                  <button className="pm-aw-btn ghost" onClick={expandAll}>
                    Expand all
                  </button>
                  {/* No + Epic in the ALL view: a new epic must belong to a specific wave. */}
                  {!isAll ? (
                    <button
                      className="pm-aw-btn"
                      onClick={() => openEd('epic', 'new', { mvp_seq: one!.seq })}
                    >
                      + Epic
                    </button>
                  ) : null}
                </div>

                <div className="pm-aw-body" style={{ ['--mc' as string]: c }}>
                  {activeTab === 'backlog' ? (
                    backlogRows.length ? (
                      <div className="board">
                        <div className="cols">
                          <span>Epic / Feature / Story</span>
                          <span className="c-pri">Priority</span>
                          <span className="r">Pts</span>
                          <span>Status</span>
                          <span />
                        </div>
                        {backlogRows}
                      </div>
                    ) : (
                      <div className="pm-aw-empty">
                        {q ? `Nothing matches “${q}”.` : 'No epics in this wave yet — add one.'}
                      </div>
                    )
                  ) : itemRows.length ? (
                    <div className="board">
                      <div
                        className="cols"
                        style={{ gridTemplateColumns: 'minmax(0,1fr) 120px 132px' }}
                      >
                        <span>Work item</span>
                        <span>Stream</span>
                        <span>Status</span>
                      </div>
                      {itemRows}
                    </div>
                  ) : (
                    <div className="pm-aw-empty">No work items mapped to this wave.</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* The drawer is rendered OUTSIDE .pm-aw, exactly as the source renders it outside .aw —
          which is why its colours are literal rather than var(--mc). */}
      <div
        className={`ed-overlay${edit ? ' on' : ''}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) closeEd();
        }}
      >
        <div className="ed-modal" role="dialog" aria-modal="true">
          <div className="ed-head">
            <span className="ttl">
              {edit ? `${edit.mode === 'edit' ? 'Edit ' : 'New '}${KIND_LABEL[edit.kind]}` : 'Edit'}
            </span>
            <button
              className="ed-ic save"
              title="Save"
              aria-label="Save"
              disabled={busy}
              onClick={() => void saveEd()}
            >
              <i className="material-icons">save</i>
            </button>
            <button className="ed-ic close" title="Close" aria-label="Close" onClick={closeEd}>
              <i className="material-icons">close</i>
            </button>
          </div>
          <div className="ed-body">
            {edit?.kind === 'epic' ? (
              <>
                <div className="ed-row">
                  <label>MVP wave</label>
                  <select className="ed-in" value={field('wave')} onChange={(e) => set('wave', e.target.value)}>
                    {waves.map((m) => (
                      <option key={m.seq} value={m.seq}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="ed-row">
                  <label>Epic name</label>
                  <input
                    className="ed-in"
                    value={field('name')}
                    placeholder="e.g. Report Optimization"
                    onChange={(e) => set('name', e.target.value)}
                  />
                </div>
                <div className="ed-row">
                  <label>Description</label>
                  <textarea
                    className="ed-in ta"
                    value={field('desc')}
                    onChange={(e) => set('desc', e.target.value)}
                  />
                </div>
                <div className="ed-row">
                  <label>Status</label>
                  <select className="ed-in" value={field('status')} onChange={(e) => set('status', e.target.value)}>
                    {STATUS_OPTS.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
              </>
            ) : null}

            {edit?.kind === 'feature' ? (
              <>
                <div className="ed-ctx">
                  Epic:{' '}
                  <b>
                    {index.epic[(edit.ctx.epic_id ?? (edit.ent as Feature | null)?.epic_id)!]?.name ??
                      '—'}
                  </b>
                </div>
                <div className="ed-row">
                  <label>Feature name</label>
                  <input
                    className="ed-in"
                    value={field('name')}
                    placeholder="e.g. Top-5 report tuning"
                    onChange={(e) => set('name', e.target.value)}
                  />
                </div>
                <div className="ed-row">
                  <label>Description</label>
                  <textarea
                    className="ed-in ta"
                    value={field('desc')}
                    onChange={(e) => set('desc', e.target.value)}
                  />
                </div>
                <div className="ed-row">
                  <label>Status</label>
                  <select className="ed-in" value={field('status')} onChange={(e) => set('status', e.target.value)}>
                    {STATUS_OPTS.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
              </>
            ) : null}

            {edit?.kind === 'story' ? (
              <>
                <div className="ed-ctx">
                  Epic:{' '}
                  <b>
                    {index.epic[(edit.ctx.epic_id ?? (edit.ent as Story | null)?.epic_id)!]?.name ??
                      '—'}
                  </b>
                  {edit.ctx.feature_id ?? (edit.ent as Story | null)?.feature_id ? (
                    <>
                      {' '}
                      &nbsp;·&nbsp; Feature:{' '}
                      <b>
                        {index.feature[
                          (edit.ctx.feature_id ?? (edit.ent as Story | null)?.feature_id)!
                        ]?.name ?? ''}
                      </b>
                    </>
                  ) : null}
                </div>
                <div className="ed-row">
                  <label>Title</label>
                  <input
                    className="ed-in"
                    value={field('title')}
                    placeholder="Short summary of the story"
                    onChange={(e) => set('title', e.target.value)}
                  />
                </div>
                <div className="ed-narr">
                  <div className="ed-row">
                    <label>As a</label>
                    <input
                      className="ed-in"
                      value={field('asa')}
                      placeholder="finance user"
                      onChange={(e) => set('asa', e.target.value)}
                    />
                  </div>
                  <div className="ed-row">
                    <label>I want</label>
                    <input
                      className="ed-in"
                      value={field('iwant')}
                      placeholder="the P&L report to load within its SLA"
                      onChange={(e) => set('iwant', e.target.value)}
                    />
                  </div>
                  <div className="ed-row">
                    <label>So that</label>
                    <input
                      className="ed-in"
                      value={field('sothat')}
                      placeholder="I stop losing time waiting on it"
                      onChange={(e) => set('sothat', e.target.value)}
                    />
                  </div>
                </div>
                <div className="ed-row">
                  <label>Acceptance criteria</label>
                  <textarea
                    className="ed-in ta"
                    value={field('ac')}
                    onChange={(e) => set('ac', e.target.value)}
                  />
                </div>
                <div className="ed-3">
                  <div className="ed-row">
                    <label>Story points</label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      className="ed-in"
                      value={field('pts')}
                      onChange={(e) => set('pts', e.target.value)}
                    />
                  </div>
                  <div className="ed-row">
                    <label>Priority</label>
                    <select className="ed-in" value={field('prio')} onChange={(e) => set('prio', e.target.value)}>
                      {['High', 'Medium', 'Low'].map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div className="ed-row">
                    <label>Status</label>
                    <select
                      className="ed-in"
                      value={field('status')}
                      onChange={(e) => set('status', e.target.value)}
                    >
                      {STATUS_OPTS.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            ) : null}

            {edit?.kind === 'task' ? (
              <>
                <div className="ed-ctx">
                  Story:{' '}
                  <b>
                    {index.story[(edit.ctx.story_id ?? (edit.ent as Task | null)?.id)!]?.title ?? '—'}
                  </b>
                </div>
                <div className="ed-row">
                  <label>Task</label>
                  <input
                    className="ed-in"
                    value={field('taskDesc')}
                    placeholder="e.g. Tune query and add index"
                    onChange={(e) => set('taskDesc', e.target.value)}
                  />
                </div>
                <div className="ed-row">
                  <label>Owner</label>
                  <input
                    className="ed-in"
                    value={field('owner')}
                    placeholder="Who does it"
                    onChange={(e) => set('owner', e.target.value)}
                  />
                </div>
                <div className="ed-row">
                  <label>Status</label>
                  <select className="ed-in" value={field('status')} onChange={(e) => set('status', e.target.value)}>
                    {STATUS_OPTS.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
              </>
            ) : null}
          </div>
          <div className="ed-foot">
            <button className="ed-btn ghost" onClick={closeEd}>
              Cancel
            </button>
            <button className="ed-btn primary" disabled={busy} onClick={() => void saveEd()}>
              <i className="material-icons">save</i>
              <span className="lbl">{busy ? 'Saving…' : 'Save'}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
